import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Wallet, RefreshCw, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle2, HelpCircle, Banknote, Loader2, Package,
} from 'lucide-react'
import { reembolsos, ReembolsoBatch, ReembolsoLine } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

const CARRIERS: Record<string, { label: string; color: string }> = {
  mrw: { label: 'MRW', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  gls: { label: 'GLS', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  cex: { label: 'Correos Express', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
}
const eur = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
const fdate = (s?: string | null) => (s ? s.slice(0, 10).split('-').reverse().join('/') : '—')

type Tab = 'liquidaciones' | 'revision' | 'impagos'

export default function ReembolsosPage() {
  const [tab, setTab] = useState<Tab>('liquidaciones')
  const toast = useToast()
  const qc = useQueryClient()

  const dash = useQuery({ queryKey: ['reembolsos-dashboard'], queryFn: reembolsos.dashboard })

  const ingestMut = useMutation({
    mutationFn: () => reembolsos.ingest(20, 300),
    onSuccess: (d) => {
      toast.success(`Ingesta: ${d.nuevas_liquidaciones} liquidación(es) nueva(s) de ${d.scanned} correos`)
      qc.invalidateQueries({ queryKey: ['reembolsos-dashboard'] })
      qc.invalidateQueries({ queryKey: ['reembolsos-batches'] })
    },
    onError: (e: Error) => toast.error(`Error en la ingesta: ${e.message}`),
  })

  const r = dash.data?.resumen || {}

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Cabecera */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
            <Wallet className="w-6 h-6 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Control de Reembolsos</h1>
            <p className="text-sm text-slate-500">Conciliación de liquidaciones COD de MRW, GLS y Correos Express</p>
          </div>
        </div>
        <button
          onClick={() => ingestMut.mutate()}
          disabled={ingestMut.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium"
        >
          {ingestMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Revisar buzón ahora
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Liquidado (total)" value={eur(r.total)} sub={`${r.batches || 0} liquidaciones`} />
        <Kpi label="Discrepancias" value={String(r.discrep || 0)} warn={(r.discrep || 0) > 0} />
        <Kpi label="Posibles impagos" value={String(dash.data?.impagos_count || 0)} sub={eur(dash.data?.impagos_total)} warn={(dash.data?.impagos_count || 0) > 0} />
        <Kpi label="Pendientes de banco" value={String(r.pend_banco || 0)} sub={`${r.cobradas || 0} cobradas`} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 mb-4">
        <TabBtn active={tab === 'liquidaciones'} onClick={() => setTab('liquidaciones')}>Liquidaciones</TabBtn>
        <TabBtn active={tab === 'revision'} onClick={() => setTab('revision')}>
          Revisión {(r.discrep || 0) > 0 && <span className="ml-1 text-xs bg-amber-500 text-white rounded-full px-1.5">{r.discrep}</span>}
        </TabBtn>
        <TabBtn active={tab === 'impagos'} onClick={() => setTab('impagos')}>
          Impagos {(dash.data?.impagos_count || 0) > 0 && <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-1.5">{dash.data.impagos_count}</span>}
        </TabBtn>
      </div>

      {tab === 'liquidaciones' && <Liquidaciones />}
      {tab === 'revision' && <Revision />}
      {tab === 'impagos' && <Impagos />}
    </div>
  )
}

function Kpi({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${warn ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'}`}>
      <div className="text-xs text-slate-500 uppercase">{label}</div>
      <div className="text-xl font-bold text-slate-800 dark:text-slate-100 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  )
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${active ? 'border-teal-600 text-teal-600 dark:text-teal-400' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
      {children}
    </button>
  )
}

function CarrierBadge({ carrier }: { carrier: string }) {
  const c = CARRIERS[carrier] || { label: carrier?.toUpperCase(), color: 'bg-slate-100 text-slate-700' }
  return <span className={`text-xs font-medium px-2 py-0.5 rounded ${c.color}`}>{c.label}</span>
}

// --------------------------------------------------------------------------- //
// Vista 1: Liquidaciones (batches) con tick de banco y detalle expandible
// --------------------------------------------------------------------------- //
function Liquidaciones() {
  const toast = useToast()
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState<number | null>(null)
  const q = useQuery({ queryKey: ['reembolsos-batches'], queryFn: () => reembolsos.listBatches(undefined, 200) })

  const bankMut = useMutation({
    mutationFn: ({ id, collected }: { id: number; collected: boolean }) =>
      reembolsos.setBankCollected(id, { collected }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reembolsos-batches'] })
      qc.invalidateQueries({ queryKey: ['reembolsos-dashboard'] })
    },
    onError: (e: Error) => toast.error(`Error: ${e.message}`),
  })

  if (q.isLoading) return <Loading />
  const batches = q.data?.batches || []
  if (!batches.length) return <Empty text="No hay liquidaciones registradas todavía." />

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
              <th className="px-3 py-2 text-left w-8"></th>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Transportista</th>
              <th className="px-3 py-2 text-center">Reembolsos</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-center">Conciliación</th>
              <th className="px-3 py-2 text-center">Cobrada en banco</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <BatchRow key={b.id} b={b} expanded={expanded === b.id}
                onToggle={() => setExpanded(expanded === b.id ? null : b.id)}
                onBank={(collected) => bankMut.mutate({ id: b.id, collected })}
                bankBusy={bankMut.isPending} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function BatchRow({ b, expanded, onToggle, onBank, bankBusy }: {
  b: ReembolsoBatch; expanded: boolean; onToggle: () => void; onBank: (c: boolean) => void; bankBusy: boolean
}) {
  const detail = useQuery({
    queryKey: ['reembolsos-batch', b.id],
    queryFn: () => reembolsos.batchDetail(b.id),
    enabled: expanded,
  })
  return (
    <>
      <tr className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/20">
        <td className="px-3 py-2 cursor-pointer" onClick={onToggle}>
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </td>
        <td className="px-3 py-2 cursor-pointer" onClick={onToggle}>{fdate(b.settlement_date || b.email_date)}</td>
        <td className="px-3 py-2"><CarrierBadge carrier={b.carrier} /></td>
        <td className="px-3 py-2 text-center">{b.line_count}</td>
        <td className="px-3 py-2 text-right font-medium">{eur(b.total_amount)}</td>
        <td className="px-3 py-2 text-center">
          <span className="text-green-600">{b.matched_count}✓</span>
          {b.discrepancy_count > 0 && <span className="text-amber-600 ml-1">{b.discrepancy_count}⚠</span>}
          {b.notfound_count > 0 && <span className="text-slate-400 ml-1">{b.notfound_count}?</span>}
        </td>
        <td className="px-3 py-2 text-center">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={b.bank_collected === 1} disabled={bankBusy}
              onChange={(e) => onBank(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer" />
            {b.bank_collected === 1 && b.bank_collected_date && (
              <span className="text-xs text-green-600">{fdate(b.bank_collected_date)}</span>
            )}
          </label>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-slate-50 dark:bg-slate-900/30 px-6 py-3">
            {detail.isLoading ? <Loading /> : <LinesTable lines={detail.data?.lines || []} />}
          </td>
        </tr>
      )}
    </>
  )
}

function LinesTable({ lines }: { lines: ReembolsoLine[] }) {
  if (!lines.length) return <div className="text-slate-400 text-sm py-2">Sin líneas.</div>
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-slate-500 uppercase">
          <th className="px-2 py-1 text-left">Pedido</th>
          <th className="px-2 py-1 text-left">Destinatario</th>
          <th className="px-2 py-1 text-right">Liquidado</th>
          <th className="px-2 py-1 text-right">Esperado</th>
          <th className="px-2 py-1 text-center">Estado</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id} className="border-t border-slate-200 dark:border-slate-700/40">
            <td className="px-2 py-1 font-mono">{l.order_reference}</td>
            <td className="px-2 py-1 truncate max-w-xs">{l.recipient}</td>
            <td className="px-2 py-1 text-right">{eur(l.settled_amount)}</td>
            <td className="px-2 py-1 text-right text-slate-500">{l.expected_amount != null ? eur(l.expected_amount) : '—'}</td>
            <td className="px-2 py-1 text-center"><StatusBadge s={l.match_status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function StatusBadge({ s }: { s: string }) {
  if (s === 'matched') return <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 className="w-3.5 h-3.5" />Conciliado</span>
  if (s === 'discrepancy') return <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3.5 h-3.5" />Discrepancia</span>
  return <span className="inline-flex items-center gap-1 text-slate-400"><HelpCircle className="w-3.5 h-3.5" />No encontrado</span>
}

// --------------------------------------------------------------------------- //
// Vista 2: Revisión (líneas con discrepancia o no encontradas)
// --------------------------------------------------------------------------- //
function Revision() {
  const q = useQuery({
    queryKey: ['reembolsos-batches-full'],
    queryFn: async () => {
      const { batches } = await reembolsos.listBatches(undefined, 200)
      const problem = batches.filter((b) => b.discrepancy_count > 0 || b.notfound_count > 0)
      const details = await Promise.all(problem.slice(0, 60).map((b) => reembolsos.batchDetail(b.id)))
      const rows: (ReembolsoLine & { carrier: string })[] = []
      details.forEach((d) => (d.lines || []).forEach((l) => {
        if (l.match_status !== 'matched') rows.push({ ...l, carrier: d.batch.carrier })
      }))
      return rows
    },
  })
  if (q.isLoading) return <Loading />
  const rows = q.data || []
  if (!rows.length) return <Empty text="Nada que revisar: todas las líneas conciliadas correctamente. 🎉" />
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-2 text-sm text-slate-500 border-b border-slate-200 dark:border-slate-700">
        Líneas que requieren revisión ({rows.length}): importe distinto al esperado o pedido no localizado.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-900/40">
              <th className="px-3 py-2 text-left">Transportista</th>
              <th className="px-3 py-2 text-left">Pedido</th>
              <th className="px-3 py-2 text-left">Destinatario</th>
              <th className="px-3 py-2 text-right">Liquidado</th>
              <th className="px-3 py-2 text-right">Esperado</th>
              <th className="px-3 py-2 text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 dark:border-slate-700/50">
                <td className="px-3 py-2"><CarrierBadge carrier={l.carrier} /></td>
                <td className="px-3 py-2 font-mono">{l.order_reference}</td>
                <td className="px-3 py-2 truncate max-w-xs">{l.recipient}</td>
                <td className="px-3 py-2 text-right font-medium">{eur(l.settled_amount)}</td>
                <td className="px-3 py-2 text-right text-slate-500">{l.expected_amount != null ? eur(l.expected_amount) : '—'}</td>
                <td className="px-3 py-2 text-center"><StatusBadge s={l.match_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Vista 3: Impagos (COD entregados sin liquidar)
// --------------------------------------------------------------------------- //
function Impagos() {
  const [days, setDays] = useState(15)
  const q = useQuery({ queryKey: ['reembolsos-impagos', days], queryFn: () => reembolsos.impagos(days, 90) })
  if (q.isLoading) return <Loading />
  const list = q.data?.impagos || []
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-2 flex items-center justify-between border-b border-slate-200 dark:border-slate-700 flex-wrap gap-2">
        <div className="text-sm text-slate-500">
          COD entregados sin liquidar (entre {days} y 90 días): <b>{q.data?.count || 0}</b> · <b>{eur(q.data?.total)}</b>
        </div>
        <label className="text-xs text-slate-500 flex items-center gap-2">
          Antigüedad mínima:
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700">
            <option value={7}>7 días</option>
            <option value={15}>15 días</option>
            <option value={30}>30 días</option>
          </select>
        </label>
      </div>
      {!list.length ? <Empty text="Sin posibles impagos en esta ventana. 🎉" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-900/40">
                <th className="px-3 py-2 text-left">Entregado</th>
                <th className="px-3 py-2 text-left">Transportista</th>
                <th className="px-3 py-2 text-left">Pedido</th>
                <th className="px-3 py-2 text-left">Destinatario</th>
                <th className="px-3 py-2 text-left">Seguimiento</th>
                <th className="px-3 py-2 text-right">Importe COD</th>
              </tr>
            </thead>
            <tbody>
              {list.map((i) => (
                <tr key={i.order_id} className="border-t border-slate-100 dark:border-slate-700/50">
                  <td className="px-3 py-2">{fdate(i.delivered_at)}</td>
                  <td className="px-3 py-2"><CarrierBadge carrier={i.carrier} /></td>
                  <td className="px-3 py-2 font-mono">{i.order_reference}</td>
                  <td className="px-3 py-2 truncate max-w-xs">{i.recipient}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{i.tracking}</td>
                  <td className="px-3 py-2 text-right font-medium text-red-600">{eur(i.cod_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Loading() {
  return <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando…</div>
}
function Empty({ text }: { text: string }) {
  return <div className="flex flex-col items-center justify-center py-12 text-slate-400"><Package className="w-8 h-8 mb-2 opacity-40" />{text}</div>
}
